<template>
  <el-dialog
    id="DetailsModal"
    title="Leader details"
    :visible.sync="showLocal"
    :width="dialogWidth"
  >
    <div class="content">
      <div class="field">
        <el-row
          :gutter="10"
          type="flex"
          style="flex-wrap: wrap;"
        >
          <el-col
            class="field"
            :span="24"
            :md="12"
          >
            <div class="label">
              {{ $t('coach.leader.detail.coachingCategory') }}
            </div>
            <div
              v-if="formattedCategory"
              class="value"
            >
              {{ formattedCategory }}
            </div>
            <div
              v-else
              class="value"
            >
              {{ $t(`coachingTier.tier_${leader.tier}`) }}
              {{ $t(`coachingTier.tier_${leader.tier}_caption`) }}
            </div>
          </el-col>

          <el-col
            class="field"
            :span="24"
            :md="12"
          >
            <div class="field">
              <div class="label">
                {{ $t('coach.leader.status') }}

                <template v-if="leader.is_invited">
                  <el-button
                    type="text"
                    class="durationBtn"
                    @click="toggleStatus"
                  >
                    {{ leader.curr_engagement_status === 'on_going' ?
                      $t('common.deactivate') : $t('common.activate')
                    }}  <i class="el-icon-edit"></i>
                  </el-button>
                </template>
              </div>
              <div>
                {{ leader.curr_engagement_status === 'on_going' ? 'Active' : 'Deactivated' }}
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <div class="field">
        <div class="label">
          {{ $t('common.email') }}
        </div>
        <div class="value">
          {{ leader.email }}
          <el-button
            size="small"
            type="text"
            style="color: #212121;"
            @click="copyLink(leader.email)"
          >
            <i class="el-icon-copy-document"></i>
          </el-button>
        </div>
      </div>

      <div class="field">
        <el-row
          :gutter="10"
          type="flex"
          style="flex-wrap: wrap;"
        >
          <el-col
            class="field"
            :span="24"
            :md="12"
          >
            <div class="label">
              {{ $t('common.duration') }}

              <template v-if="leader.is_invited">
                <el-button
                  v-if="!editDuration"
                  type="text"
                  class="durationBtn"
                  @click="editDuration = true"
                >
                  {{ $t('common.edit') }}  <i class="el-icon-edit"></i>
                </el-button>
                <el-button
                  v-else
                  type="text"
                  class="durationBtn saveBtn"
                  :loading="processLoading"
                  @click="clickDurationEdit"
                >
                  {{ $t('common.save') }}
                </el-button>
              </template>
            </div>
            <div
              v-if="!editDuration"
            >
              {{ leader.duration
                ? `${leader.duration} ${$t('coach.leader.detail.durationSessions')}` : '-' }}
            </div>
            <div v-else>
              <div class="editDescription">
                {{ $t('coach.leader.detail.confirmEdit') }}
              </div>
              <el-select
                v-model="selectedDuration"
                :value="leader.duration"
                size="small"
              >
                <el-option
                  v-for="item in durationList"
                  :key="`Option_${item.value}`"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </div>
          </el-col>
          <el-col
            class="field"
            :span="24"
            :md="12"
          >
            <div class="field">
              <div class="label">
                {{ $t('common.totalFeePayout') }}
              </div>
              <div>
                {{ formattedCategoryFee }}
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/api';
import dialog from '@/mixins/dialog';
import { mapState } from 'vuex';

export default {
  name: 'DetailsModal',

  mixins: [dialog],

  props: {
    leader: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      prices: null,
      editDuration: false,
      processLoading: false,
      selectedDuration: null,
      durationList: [
        { value: 4, label: `4 ${this.$t('coach.leader.detail.durationSessions')}` },
        { value: 7, label: `7 ${this.$t('coach.leader.detail.durationSessions')}` },
        { value: 10, label: `10 ${this.$t('coach.leader.detail.durationSessions')}` },
        { value: 13, label: `13 ${this.$t('coach.leader.detail.durationSessions')}` },
      ],
    };
  },

  computed: {
    ...mapState({
      maxInviteCt: (state) => state.user.profile.max_invite_ct,
      currInviteCt: (state) => state.user.profile.curr_invite_ct,
    }),

    formattedCategory() {
      let defaultCategory = null;

      if (process.env.VUE_APP_SERVICE === 'ing_gcg_platform') {
        defaultCategory = this.$t('coachingTier.ingLeaders');
      }

      return defaultCategory;
    },

    formattedCategoryFee() {
      let charge = this.$t('common.na');
      if (!this.leader.is_invited && this.leader.total_payment_fee) {
        charge = `$ ${this.leader.total_payment_fee}`;
      }
      return charge;
    },
  },

  watch: {
    show(newValue) {
      if (newValue) {
        this.selectedDuration = this.leader.duration;
      } else {
        this.editDuration = false;
      }
    },
  },

  methods: {
    async clickDurationEdit() {
      this.$confirm(this.$t('coach.leader.detail.confirmSave', { leader: this.leader.full_name }), this.$t('coach.leader.detail.durationChange'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        roundButton: true,
      }).then(async () => {
        try {
          this.processLoading = true;
          const sendData = { duration: this.selectedDuration };
          const response = await api.saveCoachTask(this.leader.uuid, sendData);
          this.$emit('update:leader', response.data);
          this.$emit('duration-changed');
          this.$message.success(this.$t('coach.leader.detail.durationChanged'));
          this.editDuration = false;
        } catch (err) {
          console.log(err);
          if (err.msg) {
            this.$message.error(err.msg);
          }
        } finally {
          this.processLoading = false;
        }
      });
    },

    copyLink(link) {
      try {
        this.$copyText(link);
        this.$message.success(this.$t('common.copied'));
      } catch (err) {
        console.log(err);
      }
    },

    async toggleStatus() {
      try {
        if (this.leader.curr_engagement_status !== 'on_going'
        && this.currInviteCt >= this.maxInviteCt) {
          await this.$confirm(
            this.$t('coach.leader.NoEngagementDescription'),
            this.$t('coach.leader.NoEngagement'),
            {
              confirmButtonText: this.$t('common.upgrade'),
              cancelButtonText: this.$t('common.cancel'),
            },
          );
          window.open(`${process.env.VUE_APP_CAS_CLIENT}/settings/subscription`, '_blank');
        } else {
          const newStatus = this.leader.curr_engagement_status === 'on_going' ? 'exception' : 'on_going';

          const textDescription = newStatus === 'on_going'
            ? this.$t('coach.leader.activateEngagementDescription', { leaderName: this.leader.full_name })
            : this.$t('coach.leader.deactivateEngagementDescription', { leaderName: this.leader.full_name });

          await this.$confirm(
            textDescription,
            this.$t('coach.leader.changeStatus'),
            {
              confirmButtonText: this.$t('common.confirm'),
              cancelButtonText: this.$t('common.cancel'),
            },
          );

          await api.updateLeaderEngagement(this.leader.uuid, {
            engagement_status: newStatus,
          });
          this.leader.curr_engagement_status = newStatus;
          this.$message.success(this.$t('coach.leader.changeStatusSuccess'));
        }
      } catch (err) {
        if (err === 'cancel') {
          // this.clearInput();
        } else if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#DetailsModal {
  .field {
    &:not(:last-child) {
      margin-bottom: 30px;
    }

    .label {
      margin-bottom: 5px;
      font-weight: bold;
    }

    .value {
      margin-bottom: 10px;
    }
  }

  .editDescription {
    word-break: break-word;
    color: #a1a1a1;
    font-style: italic;
    margin-bottom: 6px;
  }

  .durationBtn {
    padding: 0 5px;
    height: 17px;

    &.saveBtn {
      text-decoration: underline;
    }
  }
}
</style>
